/**
 * useAgentConversation — the shared brain of every Argo chat surface.
 *
 * Extracted from ChatView (2026-07-02) so the docked Ask-Argo drawer and the
 * full Chat view run the SAME machinery: interpret → propose → confirm-each-
 * write, autonomy auto-run gating, undo capture, and dismiss/revert memory
 * signals. WHY a shared hook rather than a second implementation: these
 * mechanics are the product's core governance surface — two hand-maintained
 * copies would inevitably drift, and a drift here is a safety bug (e.g. one
 * surface auto-running a non-undoable kind), not a style bug.
 *
 * Each hook instance owns its own transcript (the drawer and the Chat view
 * are separate conversations by design — a docked aside shouldn't rewrite
 * the main thread's history), but they share the live store context shape,
 * the backend seam, and every write path.
 */

import { useEffect, useRef, useState } from 'react';
import { subscribePeople } from '../db/people';
import { subscribeAccounts } from '../db/accounts';
import { subscribeTickets } from '../db/tickets';
import { subscribeTasks } from '../db/tasks';
import { subscribeBrainFacts } from '../brain/companyBrain';
import type { Account, BrainFact, Person, ProjectTask, Ticket } from '../db/types';
import { agentBackend } from './backend';
import { logActivity } from '../db/activity';
import { isUndoableKind, runActionWithUndo, summarizeAction } from './actions';
import { memoryNotes, recordSignal } from './memory';
import { useAuth } from '../auth/AuthProvider';
import { getAutonomy, recordConfirmStreak, subscribeAutonomy } from './autonomy';
import type { AgentAction, AgentContext, ProposedAction } from './types';

export interface ChatMessage {
  id: string;
  who: 'me' | 'argo';
  body: string;
  when: string;
  actions?: ProposedAction[];
}

export function fmtTime(d = new Date()): string {
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

let proposalSeq = 0;
const nextProposalId = () => `pa-${Date.now()}-${proposalSeq++}`;

/**
 * Pull the entity an action refers to so the chat can resolve a later
 * pronoun against it. Returns null for actions that name no resolvable
 * entity (e.g. create_person, which has no id yet).
 */
function recentFromAction(action: AgentAction): AgentContext['recent'] | null {
  switch (action.kind) {
    case 'set_prospect_stage':
    case 'set_customer_status':
    case 'schedule_follow_up':
    case 'log_activity':
      return { personId: action.personId, personName: action.personName };
    case 'move_workflow_stage':
      return { accountId: action.accountId, accountName: action.accountName };
    case 'create_document':
      return action.entityType === 'account'
        ? { accountId: action.entityId, accountName: action.entityName }
        : { personId: action.entityId, personName: action.entityName };
    case 'create_task':
      if (action.personId) return { personId: action.personId, personName: action.personName };
      if (action.accountId) return { accountId: action.accountId, accountName: action.accountName };
      return null;
    default:
      return null;
  }
}

/** The live store snapshots every conversation reads as agent context. */
export type ConversationContext = AgentContext & {
  people: Person[];
  accounts: Account[];
  tickets: Ticket[];
  tasks: ProjectTask[];
  brainFacts: BrainFact[];
};

export interface AgentConversation {
  messages: ChatMessage[];
  /** True while an interpret() round-trip is in flight. */
  busy: boolean;
  /** True while the CRM has no contacts/prospects → first-run setup mode. */
  crmEmpty: boolean;
  /** Live store snapshots (held in a ref target — always current). */
  context: ConversationContext;
  send: (text: string) => Promise<void>;
  confirm: (messageId: string, proposal: ProposedAction) => Promise<void>;
  dismiss: (messageId: string, proposal: ProposedAction) => void;
  revert: (messageId: string, proposal: ProposedAction) => Promise<void>;
  /** Batch-confirm every pending card on a message, in order. One decision
   *  for "import these 12 prospects" instead of twelve. */
  confirmAll: (m: ChatMessage) => Promise<void>;
  dismissAll: (m: ChatMessage) => void;
  /**
   * Prefill proposals: append an agent message carrying the given actions
   * as PENDING confirm cards (quick-suggestion chips route through this).
   * Never executes anything — autonomy auto-run is deliberately bypassed;
   * the operator still confirms or dismisses every card.
   */
  propose: (text: string, actions: AgentAction[]) => void;
  /**
   * Append a plain agent line to the transcript (no confirm cards) — used by
   * proactive-offer accept/dismiss to drop an honest confirmation into the
   * thread ("Researching Acme — read saved to their record.").
   */
  note: (text: string) => void;
  /** Clear the transcript back to the greeting. */
  reset: () => void;
}

export function useAgentConversation(greeting: ChatMessage): AgentConversation {
  const { user } = useAuth();
  const [messages, setMessages] = useState<ChatMessage[]>([greeting]);
  const [busy, setBusy] = useState(false);
  const [crmEmpty, setCrmEmpty] = useState(false);
  // Track autonomy changes so promoted actions trigger re-render in the host
  // (the ActionCard promotion chip reads getAutonomy/getStreak at render).
  const [, setAutonomyVersion] = useState(0);

  // Live store snapshots used as agent context. Held in a ref so the
  // interpret() call always sees the latest without re-binding handlers.
  // `recent` carries the last entity referenced so a follow-up message can
  // use a pronoun ("draft a proposal for her").
  const ctxRef = useRef<ConversationContext>({
    people: [],
    accounts: [],
    tickets: [],
    tasks: [],
    brainFacts: [],
  });

  useEffect(() => {
    const unsubs = [
      subscribePeople((people) => {
        ctxRef.current.people = people;
        setCrmEmpty(!people.some((p) => p.category === 'customer' || p.category === 'prospect'));
      }),
      subscribeAccounts((accounts) => (ctxRef.current.accounts = accounts)),
      subscribeTickets((tickets) => (ctxRef.current.tickets = tickets)),
      subscribeTasks((tasks) => (ctxRef.current.tasks = tasks)),
      subscribeBrainFacts((facts) => (ctxRef.current.brainFacts = facts)),
      subscribeAutonomy(() => setAutonomyVersion((v) => v + 1)),
    ];
    return () => unsubs.forEach((u) => u());
  }, []);

  const send = async (raw: string) => {
    const text = raw.trim();
    if (!text || busy) return;
    const userMsg: ChatMessage = { id: nextProposalId(), who: 'me', body: text, when: fmtTime() };
    setMessages((prev) => [...prev, userMsg]);
    setBusy(true);
    try {
      // Inject learned preferences so a connected worker proposes with the
      // operator's track record in mind. Cheap read; [] on any failure.
      ctxRef.current.memory = await memoryNotes();
      const reply = await agentBackend.interpret(text, ctxRef.current);
      // Remember the last entity referenced so the next turn can use a
      // pronoun ("…a proposal for her"). Walk actions in order; the last
      // one that names an entity wins.
      for (const action of reply.actions) {
        const r = recentFromAction(action);
        if (r) ctxRef.current.recent = r;
      }
      const proposals = await Promise.all(
        reply.actions.map(async (action) => {
          const autonomy = getAutonomy(action.kind);
          const proposal: ProposedAction = {
            id: nextProposalId(),
            action,
            status: 'pending' as const,
          };

          // If autonomy is 'auto', execute immediately — but ONLY for kinds
          // that can be reversed. runActionWithUndo writes *before* it can
          // return an undo, so the reversibility decision has to be made on the
          // kind up front: gating on the *returned* undo meant a non-undoable
          // kind like log_activity still wrote to the store while the card was
          // left 'pending', so a manual Confirm ran it a second time (double
          // write). Non-undoable kinds skip auto entirely and stay pending for
          // a single manual confirm.
          if (autonomy === 'auto' && isUndoableKind(action.kind)) {
            try {
              const { result, undo } = await runActionWithUndo(action);
              // The write has happened — reflect that honestly. Even in the rare
              // case a normally-undoable update had no prior value to restore
              // (undo === null), mark it confirmed so a manual Confirm can't
              // re-run it; the Revert affordance simply won't render.
              proposal.status = 'confirmed';
              proposal.result = result;
              proposal.undo = undo;
            } catch {
              // Auto-run failed; fall back to 'pending' for manual confirmation.
              proposal.status = 'pending';
            }
          }
          return proposal;
        }),
      );

      const agentMsg: ChatMessage = {
        id: nextProposalId(),
        who: 'argo',
        body: reply.text,
        when: fmtTime(),
        actions: proposals,
      };
      setMessages((prev) => [...prev, agentMsg]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          id: nextProposalId(),
          who: 'argo',
          body: `Something went wrong interpreting that: ${(err as Error).message}`,
          when: fmtTime(),
        },
      ]);
    } finally {
      setBusy(false);
    }
  };

  /** Patch one proposed action inside the messages tree. */
  const patchProposal = (
    messageId: string,
    proposalId: string,
    patch: Partial<ProposedAction>,
  ) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.id !== messageId || !m.actions
          ? m
          : {
              ...m,
              actions: m.actions.map((p) => (p.id === proposalId ? { ...p, ...patch } : p)),
            },
      ),
    );
  };

  const confirm = async (messageId: string, proposal: ProposedAction) => {
    patchProposal(messageId, proposal.id, { status: 'confirmed', result: '…' });
    try {
      const { result, undo } = await runActionWithUndo(proposal.action);
      patchProposal(messageId, proposal.id, { status: 'confirmed', result, undo });
      // Record this confirmation to build a streak toward auto-promotion.
      recordConfirmStreak(proposal.action.kind);
    } catch (err) {
      patchProposal(messageId, proposal.id, {
        status: 'error',
        result: (err as Error).message,
      });
    }
  };

  const revert = async (messageId: string, proposal: ProposedAction) => {
    if (!proposal.undo) return;
    patchProposal(messageId, proposal.id, { result: '…' });
    try {
      await proposal.undo();
      patchProposal(messageId, proposal.id, { status: 'reverted', result: 'Reverted.' });
      // Record the undo in the governance ledger so the audit trail shows the
      // full story (executed → undone), not just the write. Reuses the same
      // summary + subjectId as the proposal so entries join up. Best-effort —
      // logging can never break the revert it records.
      try {
        const sum = summarizeAction(proposal.action);
        void logActivity({
          event: 'undone',
          summary: [sum.verb, sum.detail].filter(Boolean).join(' — '),
          source: 'chat',
          kind: proposal.action.kind,
          subjectId: proposal.id,
        }).catch(() => undefined);
      } catch {
        // Ledger write is best-effort; the undo itself already succeeded.
      }
      // Reverts are the strongest memory signal — the write happened and
      // the operator unwound it.
      void recordSignal(proposal.action, 'reverted', user?.displayName);
    } catch (err) {
      patchProposal(messageId, proposal.id, {
        result: `Revert failed: ${(err as Error).message}`,
      });
    }
  };

  const dismiss = (messageId: string, proposal: ProposedAction) => {
    patchProposal(messageId, proposal.id, { status: 'dismissed' });
    // Dismissals are a memory signal: repeated ones become a preference
    // note the worker sees. Fire-and-forget; never blocks the UI.
    void recordSignal(proposal.action, 'dismissed', user?.displayName);
  };

  const confirmAll = async (m: ChatMessage) => {
    for (const p of (m.actions ?? []).filter((x) => x.status === 'pending')) {
      // Sequential on purpose: cards run in the order they were proposed.
      // eslint-disable-next-line no-await-in-loop
      await confirm(m.id, p);
    }
  };

  const dismissAll = (m: ChatMessage) => {
    for (const p of (m.actions ?? []).filter((x) => x.status === 'pending')) {
      dismiss(m.id, p);
    }
  };

  const propose = (text: string, actions: AgentAction[]) => {
    if (actions.length === 0) return;
    // Track the referenced entity so the next turn can use a pronoun,
    // exactly like an interpreted reply would.
    for (const action of actions) {
      const r = recentFromAction(action);
      if (r) ctxRef.current.recent = r;
    }
    const proposals: ProposedAction[] = actions.map((action) => ({
      id: nextProposalId(),
      action,
      status: 'pending' as const,
    }));
    setMessages((prev) => [
      ...prev,
      {
        id: nextProposalId(),
        who: 'argo',
        body: text,
        when: fmtTime(),
        actions: proposals,
      },
    ]);
  };

  const note = (text: string) => {
    if (!text.trim()) return;
    setMessages((prev) => [
      ...prev,
      { id: nextProposalId(), who: 'argo', body: text, when: fmtTime() },
    ]);
  };

  const reset = () => {
    setMessages([greeting]);
  };

  return {
    messages,
    busy,
    crmEmpty,
    context: ctxRef.current,
    send,
    confirm,
    dismiss,
    revert,
    confirmAll,
    dismissAll,
    propose,
    note,
    reset,
  };
}
